// Flattens the picker's Year > Cluster > Topic > Subtopic tree into one list of
// rows per STRAND.
//
// The tutors asked for this: opening four accordions to reach a worksheet was
// the main complaint about the picker. The curriculum path is not thrown away,
// it moves onto the row, where it reads as a label instead of as four clicks.
//
// STRAND IS THE TOP LEVEL, NOT YEAR. A strand appears in several year levels,
// so its rows are gathered across all of them and the year travels on the row.
// That is the whole point: a tutor thinks "we need to fix Measurement", not
// "we need to fix Year 5, then Year 6, then Year 7 Measurement".
//
// Shared by the picker and by the report's remediation tab so the two cannot
// disagree about what a student is owed.

import type {
  BmdSkillbuilderItem,
  BmdSkillbuilderYear,
} from "../types/bmd-skillbuilder.types"

/** One worksheet, with the curriculum path it came from carried alongside. */
export type BmdSkillbuilderRow = {
  item: BmdSkillbuilderItem
  strand_order: number
  year_level_id: number | null
  year_name: string
  year_order: number
  strand_id: number | null
  strand_name: string
  topic_name: string
  sub_topics_name: string
}

export type BmdSkillbuilderStrandGroup = {
  strand_id: number | null
  strand_name: string
  strand_order: number
  rows: BmdSkillbuilderRow[]
}

/**
 * How the cluster headings are ordered. THE ONE PLACE TO CHANGE IT.
 *
 *   CURRICULUM  the order the content team lists clusters in, taken from
 *               `strand`.`order`. What the tutors are used to seeing.
 *
 *   LOWEST_YEAR the cluster reaching furthest down the year levels first, so
 *               the oldest gap leads. Useful for deciding what to teach first,
 *               and what this was built with originally.
 *
 * Rows WITHIN a cluster always run earliest year first either way: a chain of
 * prerequisites only reads bottom-up.
 */
export const CLUSTER_ORDER: "CURRICULUM" | "LOWEST_YEAR" = "CURRICULUM"

/** One labelled step of the curriculum path, for rendering. */
export type BmdSkillbuilderPathPart = {
  label: string
  value: string
}

/**
 * The curriculum path shown under a worksheet's name, as labelled parts.
 *
 * EVERY level is shown, even where two of them hold the same words. In this
 * curriculum the topic is usually identical to the strand: of 488 stranded rows
 * in the tree, 364 repeat it. Suppressing the repeat read as the level being
 * MISSING rather than as tidiness, so all of them are printed and each is
 * labelled instead. Four long phrases separated by dots are genuinely hard to
 * tell apart; "Topic:" and "Subtopic:" cost little and remove the guesswork.
 *
 * The strand is not in here: it is the heading the row already sits under on
 * both surfaces that use this.
 */
export function rowPathParts(
  row: BmdSkillbuilderRow
): BmdSkillbuilderPathPart[] {
  const parts: BmdSkillbuilderPathPart[] = []
  if (row.year_name) parts.push({ label: "Year", value: row.year_name })
  if (row.topic_name) parts.push({ label: "Topic", value: row.topic_name })
  if (row.sub_topics_name) {
    parts.push({ label: "Subtopic", value: row.sub_topics_name })
  }
  return parts
}

/** The same path as plain text, for titles and anywhere markup will not do. */
export function rowPathLabel(row: BmdSkillbuilderRow): string {
  return rowPathParts(row)
    .map(p => (p.label === "Year" ? p.value : `${p.label}: ${p.value}`))
    .join(" · ")
}

/**
 * Groups every worksheet in the picker under its strand.
 *
 * Rows within a cluster run BOTTOM UP, on the curriculum's own year_order:
 * Reception, then Year 1, and so on. That is the order a tutor teaches in,
 * because the earliest year a student is broken in holds up everything above it.
 *
 * The CLUSTERS themselves are ordered by CLUSTER_ORDER above. Ties break on
 * name either way, so the list does not reshuffle as a student's results
 * change.
 */
export function groupRowsByStrand(
  years: BmdSkillbuilderYear[]
): BmdSkillbuilderStrandGroup[] {
  const groups = new Map<
    string,
    BmdSkillbuilderStrandGroup & { firstYearOrder: number }
  >()

  years.forEach((year, yearIndex) => {
    for (const cluster of year.clusters) {
      // Keyed by id where there is one, by name otherwise: an unstranded skill
      // arrives with a null id and would otherwise collide with every other.
      const key =
        cluster.strand_id != null
          ? `id:${cluster.strand_id}`
          : `name:${cluster.strand_name || "other"}`

      if (!groups.has(key)) {
        groups.set(key, {
          strand_id: cluster.strand_id ?? null,
          strand_name: cluster.strand_name || "Other skills",
          strand_order: cluster.strand_order ?? 0,
          rows: [],
          firstYearOrder: year.year_order ?? yearIndex,
        })
      }
      const group = groups.get(key)!
      // A strand met again in an EARLIER year takes that year as its position.
      const thisYearOrder = year.year_order ?? yearIndex
      if (thisYearOrder < group.firstYearOrder) {
        group.firstYearOrder = thisYearOrder
      }

      for (const topic of cluster.topics) {
        for (const sub of topic.sub_topics) {
          for (const item of sub.skillbuilders) {
            group.rows.push({
              item,
              year_level_id: year.year_level_id ?? null,
              year_name: year.year_name || "",
              year_order: year.year_order ?? yearIndex,
              strand_id: cluster.strand_id ?? null,
              strand_name: cluster.strand_name || "Other skills",
              strand_order: cluster.strand_order ?? 0,
              topic_name: topic.topic_name || "",
              sub_topics_name: sub.sub_topics_name || "",
            })
          }
        }
      }
    }
  })

  return Array.from(groups.values())
    .sort((a, b) =>
      CLUSTER_ORDER === "CURRICULUM"
        ? a.strand_order - b.strand_order ||
          a.strand_name.localeCompare(b.strand_name)
        : a.firstYearOrder - b.firstYearOrder ||
          a.strand_name.localeCompare(b.strand_name)
    )
    .map(({ firstYearOrder, ...group }) => ({
      ...group,
      rows: group.rows.sort(
        (a, b) =>
          a.year_order - b.year_order ||
          a.item.skill_name.localeCompare(b.item.skill_name)
      ),
    }))
}

/** Every row across every strand, for counting and for select-all. */
export function allRows(
  groups: BmdSkillbuilderStrandGroup[]
): BmdSkillbuilderRow[] {
  return groups.flatMap(g => g.rows)
}

/**
 * Whether a row can be added to the lesson right now: it has a worksheet, it is
 * not already assigned, and it is not already staged in this visit.
 */
export function isStageable(
  row: BmdSkillbuilderRow,
  stagedIds: Set<number>
): boolean {
  const item = row.item
  return (
    item.has_skillbuilder &&
    item.skillbuilder_id != null &&
    item.status === "NOT_ASSIGNED" &&
    !stagedIds.has(item.skillbuilder_id)
  )
}
